import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, DeniedState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowRightLeft, Ban, CheckCircle2, Cloud, Loader2 } from "lucide-react";

export default function Tenants() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  // Local ref so we can call refresh() without React complaining about deps.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busyModuleId, setBusyModuleId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/tenants"); setItems(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!user?.is_super_admin) return <div className="max-w-7xl mx-auto px-6 py-10"><DeniedState requiredRoles={["Super Admin"]} /></div>;

  const switchTo = async (tid) => {
    try { await api.post(`/admin/switch-tenant?tenant_id=${tid}`); toast.success("Switched tenant"); await refresh(); nav("/dashboard"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleSuspend = async (t) => {
    const action = t.suspended ? "unsuspend" : "suspend";
    if (!t.suspended && !window.confirm(`Suspend "${t.name}"?\n\nAll non-super-admin users of this tenant will lose access at their next request until reinstated.`)) return;
    try {
      await api.post(`/admin/tenants/${t.id}/${action}`);
      toast.success(t.suspended ? "Tenant reinstated" : "Tenant suspended");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleAssessments = async (t) => {
    const enabled = !(t.modules?.assessments !== false); // flip current state
    setBusyModuleId(t.id);
    try {
      await api.patch(`/admin/tenants/${t.id}/modules`, { module: "assessments", enabled });
      toast.success(`Assessment module ${enabled ? "enabled" : "disabled"} for ${t.name}`);
      // Refresh both the local tenant list AND the AuthContext so the sidebar
      // + Assessments page pick up the new modules.assessments immediately —
      // without this, super-admins see the toggle flip but their own nav
      // stays stale until a hard refresh.
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusyModuleId(null); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        kicker="Super admin · cross-customer"
        title="All Tenants"
        description="Full visibility across every customer. Switch context to view any tenant's register, users, and reports. Suspend or reinstate a whole tenant at any time."
        actions={<Button className="bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => setOpen(true)} data-testid="btn-new-tenant"><Plus className="h-4 w-4 mr-1.5" /> New Tenant</Button>}
      />
      {loading ? <LoadingSkeleton /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => {
            const isCurrent = t.id === user.tenant_id;
            return (
              <Card key={t.id} className={`p-5 ${t.suspended ? "border-rose-300 bg-rose-50/40" : ""}`} data-testid={`tenant-${t.slug}`}>
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-md shrink-0" style={{ background: t.primary_color || "#0E7490" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-slate-900 truncate">{t.name}</div>
                    <div className="text-xs font-mono text-slate-500">{t.slug}</div>
                  </div>
                  {t.suspended ? (
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-rose-100 text-rose-800 border border-rose-200" data-testid={`tenant-status-${t.slug}`}>SUSPENDED</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`tenant-status-${t.slug}`}>ACTIVE</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                  <div className="rounded bg-slate-50 border p-2"><div className="font-mono uppercase text-[9px] text-slate-500">Users</div><div className="font-display font-bold text-lg tabular-nums">{t.user_count}</div></div>
                  <div className="rounded bg-slate-50 border p-2"><div className="font-mono uppercase text-[9px] text-slate-500">Risks</div><div className="font-display font-bold text-lg tabular-nums">{t.risk_count}</div></div>
                </div>
                {/* Module switches */}
                <div className="mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Modules</div>
                  <button
                    type="button"
                    onClick={() => toggleAssessments(t)}
                    disabled={busyModuleId === t.id}
                    data-testid={`module-assessments-${t.slug}`}
                    className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all ${
                      t.modules?.assessments !== false
                        ? "bg-cyan-50 border-cyan-200 hover:border-cyan-400"
                        : "bg-slate-50 border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <Cloud className={`h-4 w-4 shrink-0 ${t.modules?.assessments !== false ? "text-cyan-700" : "text-slate-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold text-slate-900">M365 Assessment</div>
                      <div className="text-[10.5px] font-mono text-slate-500">CIS v7 benchmark scans</div>
                    </div>
                    {busyModuleId === t.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold ${
                          t.modules?.assessments !== false
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            : "bg-slate-200 text-slate-600 border border-slate-300"
                        }`}
                        data-testid={`module-assessments-state-${t.slug}`}
                      >
                        {t.modules?.assessments !== false ? "ON" : "OFF"}
                      </span>
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={() => switchTo(t.id)} disabled={t.suspended && !isCurrent} data-testid={`switch-${t.slug}`}>
                    <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" /> Switch
                  </Button>
                  {t.suspended ? (
                    <Button size="sm" variant="outline" className="text-emerald-700 hover:text-emerald-800 border-emerald-300" onClick={() => toggleSuspend(t)} data-testid={`btn-unsuspend-tenant-${t.slug}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Reinstate
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="text-rose-700 hover:text-rose-800 border-rose-300" onClick={() => toggleSuspend(t)} disabled={isCurrent} title={isCurrent ? "Switch to another tenant first" : ""} data-testid={`btn-suspend-tenant-${t.slug}`}>
                      <Ban className="h-3.5 w-3.5 mr-1.5" /> Suspend
                    </Button>
                  )}
                </div>
                {isCurrent && <div className="mt-2 text-[10px] font-mono text-slate-500">This is your currently active tenant.</div>}
              </Card>
            );
          })}
        </div>
      )}
      {open && <NewTenantDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewTenantDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", slug: "", primary_color: "#0E7490" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name || !form.slug) return toast.error("Name and slug required");
    setBusy(true);
    try { await api.post("/admin/tenants", form); toast.success("Tenant created"); onSaved?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="tenant-dialog">
        <DialogHeader><DialogTitle className="font-display">Enrol new customer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Company name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} data-testid="tenant-name" /></div>
          <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="font-mono" data-testid="tenant-slug" /></div>
          <div><Label>Primary color</Label><Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-20 h-10 p-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="tenant-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
